/**
 * Document vault — searchable list grouped by category, PDF-tinted icon
 * tiles. Wired: GET /api/account/documents. Port of screens-account.jsx
 * Documents.
 * Uploads (2026-07-18): holders of write on admin.documents (Hon. Secretary's
 * locked bye-law 140(17) grant; admins) get "Upload" — expo-document-picker
 * is LAZY-loaded (absent on OTA'd 0.2.0 clients → button hidden), PDF/images
 * only, 5 MB cap, stored as data-URIs like every other upload. Writers can
 * also remove documents; both actions land on the audit trail.
 */
import { router } from 'expo-router';
import React from 'react';
import { Platform, View } from 'react-native';

import { space } from '@bero/tokens';
import { fmtWhen, str, useGateList } from '@/components/gate/api';
import { goBack, IconCircle, SmallButton } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import {
  Card, AppText, Button, Chip, EmptyState, Field, Input, Screen, SectionLabel,
  Sheet, Skeleton, TopBar,
} from '@/ui';

type Doc = { id: string; name: string; kind: string; size: string; when: string; category: string };

const CATEGORIES = ['minutes', 'legal', 'certificate', 'contract', 'circular', 'finance', 'general'] as const;
const CAT_KEY: Record<string, DictKey> = {
  minutes: 'doc_cat_minutes', legal: 'doc_cat_legal', certificate: 'doc_cat_certificate',
  contract: 'doc_cat_contract', circular: 'doc_cat_circular', finance: 'doc_cat_finance', general: 'doc_cat_general',
};
const MAX_BYTES = 5 * 1024 * 1024;

function toDoc(d: unknown, i: number): Doc {
  return {
    id: str(d, 'id') ?? `d${i}`,
    name: str(d, 'name', 'title') ?? 'Document',
    kind: str(d, 'kind', 'type', 'format') ?? 'PDF',
    size: str(d, 'size', 'sizeLabel') ?? '',
    when: fmtWhen(str(d, 'createdAt', 'uploadedAt'), str(d, 'when')),
    category: str(d, 'category', 'cat', 'group') ?? 'General',
  };
}

type PickedFile = { name: string; sizeLabel: string; dataUri: string };

export default function DocumentsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, state, reload } = useGateList<unknown>('/api/account/documents', 'documents');
  const [q, setQ] = React.useState('');
  const [canWrite, setCanWrite] = React.useState(false);
  const [pickerReady, setPickerReady] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  // upload sheet
  const [open, setOpen] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [category, setCategory] = React.useState<string>('general');
  const [picked, setPicked] = React.useState<PickedFile | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    api.get<{ perms?: Record<string, string[]> }>('/api/positions/me')
      .then((me) => setCanWrite((me.perms?.['admin.documents'] ?? []).includes('write')))
      .catch(() => {});
    import('expo-document-picker')
      .then((D) => setPickerReady(typeof D.getDocumentAsync === 'function'))
      .catch(() => {});
  }, []);

  const pickFile = async () => {
    try {
      const D = await import('expo-document-picker');
      const res = await D.getDocumentAsync({
        type: ['application/pdf', 'image/*'],
        copyToCacheDirectory: true,
        multiple: false,
      });
      if (res.canceled || !res.assets?.[0]) return;
      const asset = res.assets[0];
      if ((asset.size ?? 0) > MAX_BYTES) {
        setNotice(t('doc_too_big'));
        return;
      }
      let dataUri: string | null = null;
      if (asset.uri.startsWith('data:')) {
        dataUri = asset.uri;
      } else if (Platform.OS === 'web' && asset.file) {
        dataUri = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = reject;
          reader.readAsDataURL(asset.file as Blob);
        });
      } else {
        const FS = await import('expo-file-system/legacy');
        const b64 = await FS.readAsStringAsync(asset.uri, { encoding: FS.EncodingType.Base64 });
        dataUri = `data:${asset.mimeType ?? 'application/pdf'};base64,${b64}`;
      }
      const kb = Math.round((asset.size ?? (dataUri.length * 3) / 4) / 1024);
      setPicked({
        name: asset.name ?? 'document',
        sizeLabel: kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`,
        dataUri,
      });
      if (!title.trim() && asset.name) setTitle(asset.name.replace(/\.[a-z0-9]+$/i, ''));
    } catch {
      setNotice(t('doc_picker_error'));
    }
  };

  const upload = () => {
    if (busy || !picked) return;
    if (title.trim().length < 3) { setNotice(t('doc_title_error')); return; }
    setBusy(true);
    api.post<unknown>('/api/account/documents', {
      title: title.trim(), category, fileData: picked.dataUri,
    })
      .then(() => { setNotice(t('doc_uploaded', { name: title.trim() })); void reload(); })
      .catch((e) => setNotice((e as { error?: string })?.error ?? t('doc_upload_error')))
      .finally(() => { setBusy(false); setOpen(false); setPicked(null); setTitle(''); });
  };

  const remove = (d: Doc) => {
    api.post<unknown>(`/api/account/documents/${d.id}/delete`, {})
      .then(() => { setNotice(t('doc_removed', { name: d.name })); void reload(); })
      .catch((e) => setNotice((e as { error?: string })?.error ?? t('doc_remove_error')));
  };

  const docs = items.map(toDoc).filter((d) => d.name.toLowerCase().includes(q.trim().toLowerCase()));
  const categories = [...new Set(docs.map((d) => d.category))];

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('svc_documents')}
        onBack={goBack}
        right={canWrite && pickerReady ? (
          <SmallButton label={t('doc_upload')} icon="plus" onPress={() => {
            setTitle(''); setCategory('general'); setPicked(null); setNotice(null); setOpen(true);
          }} />
        ) : undefined}
      />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {notice ? (
          <AppText variant="bodySm" weight="500" style={{ marginBottom: space.s3, color: colors.primary700 }}>
            {notice}
          </AppText>
        ) : null}
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} h={68} />)}
          </View>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Icon name="file-text" size={40} color={colors.textTertiary} />}
            title={state === 'error' ? t('doc_err_title') : t('doc_empty_title')}
            body={state === 'error' ? t('doc_err_body') : t('doc_empty_body')}
          />
        ) : (
          <>
            <Input value={q} onChangeText={setQ} placeholder={t('doc_search_ph')} style={{ marginBottom: space.s4 }} />
            {docs.length === 0 ? (
              <EmptyState
                icon={<Icon name="search" size={40} color={colors.textTertiary} />}
                title={t('res_no_matches')}
                body={t('doc_no_matches_body', { q: q.trim() })}
              />
            ) : (
              categories.map((cat, ci) => (
                <View key={cat}>
                  <SectionLabel style={{ marginTop: ci === 0 ? 0 : space.s5 }}>{CAT_KEY[cat.toLowerCase()] ? t(CAT_KEY[cat.toLowerCase()]) : cat}</SectionLabel>
                  <View style={{ gap: space.s2 }}>
                    {docs.filter((d) => d.category === cat).map((d) => (
                      <Card
                        key={d.id}
                        onPress={() => router.push({ pathname: '/account/doc-viewer', params: { name: d.name, id: d.id } })}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                          <IconCircle name="file-text" bg={colors.danger50} fg={colors.danger500} />
                          <View style={{ flex: 1, minWidth: 0 }}>
                            <AppText variant="bodyMd" weight="500">{d.name}</AppText>
                            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                              {[d.kind, d.size, d.when].filter(Boolean).join(' · ')}
                            </AppText>
                          </View>
                          {canWrite ? (
                            <AppText variant="bodySm" weight="500" style={{ color: colors.danger500 }}
                              onPress={() => remove(d)}>
                              {t('doc_remove')}
                            </AppText>
                          ) : (
                            <Icon name="chevron-right" size={18} color={colors.textTertiary} />
                          )}
                        </View>
                      </Card>
                    ))}
                  </View>
                </View>
              ))
            )}
          </>
        )}
      </Screen>

      {/* upload */}
      <Sheet visible={open} onClose={() => setOpen(false)}>
        <SectionLabel>{t('doc_upload_hdr')}</SectionLabel>
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
          {t('doc_upload_sub')}
        </AppText>
        <View style={{ marginTop: space.s4 }}>
          {picked ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
              <IconCircle name="file-text" bg={colors.primary100} fg={colors.primary700} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <AppText variant="bodyMd" weight="500" numberOfLines={1}>{picked.name}</AppText>
                <AppText variant="bodySm" tone="tertiary">{picked.sizeLabel}</AppText>
              </View>
              <SmallButton label={t('doc_change')} onPress={() => void pickFile()} />
            </View>
          ) : (
            <Button title={t('doc_choose_file')} kind="secondary" onPress={() => void pickFile()} />
          )}
        </View>
        <View style={{ marginTop: space.s3 }}>
          <Field label={t('doc_title_field')}>
            <Input value={title} onChangeText={setTitle} placeholder={t('doc_title_ph')} maxLength={200} />
          </Field>
        </View>
        <AppText variant="bodySm" weight="500" tone="secondary" style={{ marginTop: space.s3, marginBottom: space.s2 }}>
          {t('hd_category')}
        </AppText>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
          {CATEGORIES.map((c) => (
            <Chip key={c} label={t(CAT_KEY[c])} active={category === c} onPress={() => setCategory(c)} />
          ))}
        </View>
        <Button title={busy ? t('doc_uploading') : t('doc_upload')} disabled={busy || !picked}
          style={{ marginTop: space.s4 }} onPress={upload} />
      </Sheet>
    </Screen>
  );
}
